/**
 * FAQ - Frequently Asked Questions
 */

import React, { useState } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Paper
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionIcon
} from '@mui/icons-material'

interface FAQItem {
  id: string
  question: string
  answer: string
  category: string
  tags: string[]
}

const faqItems: FAQItem[] = [
  {
    id: '1',
    question: 'How often does data sync between systems?',
    answer: 'Data synchronization frequency varies by system: Lightspeed syncs every 15 minutes for incremental updates, Xero syncs hourly, and Skimmer (US only) syncs hourly. Full syncs run daily during off-peak hours to ensure complete data consistency.',
    category: 'Sync',
    tags: ['sync', 'frequency', 'schedule', 'lightspeed', 'xero']
  },
  {
    id: '2',
    question: 'What do the different exception severity levels mean?',
    answer: 'Exceptions are categorized into three severity levels: Critical (red) - requires immediate attention as it may impact business operations; Warning (yellow) - should be addressed soon but not immediately impacting operations; Info (blue) - informational notices that may require review but are not errors.',
    category: 'Exceptions',
    tags: ['exceptions', 'severity', 'critical', 'warning', 'info']
  },
  {
    id: '3',
    question: 'Why is some data missing after a sync?',
    answer: 'Missing data can occur due to several reasons: 1) The data may not meet sync criteria (e.g., date ranges), 2) There may be validation errors preventing sync, 3) The source system may have restrictions or filters applied, 4) Network issues during sync. Check the sync logs and exceptions for specific details.',
    category: 'Data',
    tags: ['missing data', 'sync issues', 'troubleshooting']
  },
  {
    id: '4',
    question: 'How do I export data from the Inspector?',
    answer: 'You can export data in several ways: 1) From any data table, click the Export button to download as Excel, 2) Use the Tools section for specialized exports like COGS reports, 3) The Data Explorer allows bulk exports with filters applied. All exports respect your current filters and selections.',
    category: 'Features',
    tags: ['export', 'excel', 'download', 'reports']
  },
  {
    id: '5',
    question: 'What is the COGS Review tool?',
    answer: 'The COGS (Cost of Goods Sold) Review tool analyzes your product pricing and costs to identify margin issues. It flags products with negative margins, unusually high or low margins, and missing cost information. This helps ensure accurate pricing and profitability tracking.',
    category: 'Tools',
    tags: ['cogs', 'pricing', 'margins', 'profitability']
  },
  {
    id: '6',
    question: 'How do I resolve "Duplicate Sales" exceptions?',
    answer: 'Duplicate sales exceptions flag sales at the same outlet that may be the same real-world transaction recorded twice - e.g. a sync replay or an operator pressing Enter twice. The detector requires an "anchor" signal (same customer, matching service-job identity, or very close timestamp) on top of matching content, so different customers buying the same popular product at the same outlet are not flagged. Each group is scored and banded as "highly likely" (≥85%) or "likely" (60–84%). To resolve: 1) Open the group in the Duplicate Sales tool and compare members side-by-side, 2) Check if one is a legitimate separate sale (different service date, pool address or line-item notes), 3) Check if one is a void/credit-note pair that was not linked, 4) Correct or void the erroneous record in Lightspeed.',
    category: 'Exceptions',
    tags: ['duplicate', 'sales', 'exceptions', 'resolution']
  },
  {
    id: '7',
    question: 'Why are my Xero contacts not linking to Lightspeed clients?',
    answer: 'Contact linking requires matching identifiers between systems. Common issues include: 1) Different email addresses in each system, 2) Company name variations, 3) One system using individual names while the other uses company names. Use the Client Matching tool to identify and resolve mismatches.',
    category: 'Integration',
    tags: ['xero', 'lightspeed', 'clients', 'contacts', 'linking']
  },
  {
    id: '8',
    question: 'What does "Stale Data" mean?',
    answer: 'Stale data indicates that information hasn\'t been updated within the expected timeframe (usually 24 hours). This could mean: 1) The sync is failing, 2) No new data is available from the source, 3) Connection issues with the source system. Check the Sync Status page for details.',
    category: 'Data',
    tags: ['stale data', 'sync', 'updates', 'freshness']
  },
  {
    id: '9',
    question: 'How do I trigger a manual sync?',
    answer: 'Manual syncs can be triggered from multiple locations: 1) The Sync Status page in Data Explorer has "Trigger Sync" buttons for each system, 2) Admin users can trigger syncs from the Admin panel, 3) Some tools automatically trigger relevant syncs after making changes. Note that manual syncs are rate-limited to prevent system overload.',
    category: 'Sync',
    tags: ['manual sync', 'trigger', 'refresh', 'update']
  },
  {
    id: '10',
    question: 'What permissions do I need to access different features?',
    answer: 'The Inspector has role-based access: Viewers can see all data and run reports but cannot make changes; Admin users can trigger syncs, manage exceptions, and access all tools; Team members have intermediate access with some administrative capabilities. Contact your system administrator to adjust permissions.',
    category: 'Access',
    tags: ['permissions', 'roles', 'access', 'security']
  },
  {
    id: '11',
    question: 'How do I interpret the Health Score?',
    answer: 'The Health Score (0-100) indicates overall data quality: 90-100 is Excellent (green), 70-89 is Good (blue), 50-69 is Fair (yellow), below 50 is Poor (red). The score considers factors like exception count, data freshness, sync success rate, and data completeness. Click on the score for a detailed breakdown.',
    category: 'Metrics',
    tags: ['health score', 'data quality', 'metrics', 'monitoring']
  },
  {
    id: '12',
    question: 'What should I do if a sync keeps failing?',
    answer: 'For persistent sync failures: 1) Check the error message in sync logs for specific issues, 2) Verify API credentials haven\'t expired, 3) Ensure the source system is accessible and not undergoing maintenance, 4) Check for API rate limits or quotas, 5) Contact support if the issue persists for more than 24 hours.',
    category: 'Troubleshooting',
    tags: ['sync failure', 'errors', 'troubleshooting', 'api']
  }
]

const FAQ: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const categories = ['all', ...Array.from(new Set(faqItems.map(item => item.category)))]

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false)
  }

  const filteredFAQs = faqItems.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory
    
    return matchesSearch && matchesCategory
  })

  const getCategoryColor = (category: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
      'Sync': 'primary',
      'Exceptions': 'error',
      'Data': 'info',
      'Features': 'secondary',
      'Tools': 'success',
      'Integration': 'warning',
      'Access': 'primary',
      'Metrics': 'info',
      'Troubleshooting': 'error'
    }
    return colors[category] || 'default'
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Frequently Asked Questions
      </Typography>
      
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <Chip
              key={cat}
              label={cat === 'all' ? 'All Categories' : cat}
              onClick={() => setSelectedCategory(cat)}
              color={cat === selectedCategory ? 'primary' : 'default'}
              variant={cat === selectedCategory ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      </Stack>

      {filteredFAQs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <QuestionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No FAQs found matching your search.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try different keywords or browse all categories.
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {filteredFAQs.length} question{filteredFAQs.length !== 1 ? 's' : ''}
          </Typography>
          
          {filteredFAQs.map((faq) => (
            <Accordion
              key={faq.id}
              expanded={expandedPanel === faq.id}
              onChange={handleAccordionChange(faq.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1">
                      {faq.question}
                    </Typography>
                  </Box>
                  <Chip
                    label={faq.category}
                    size="small"
                    color={getCategoryColor(faq.category)}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" paragraph>
                    {faq.answer}
                  </Typography>
                  
                  {faq.tags.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {faq.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1 }}
                            onClick={() => setSearchQuery(tag)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      
      <Paper sx={{ p: 2, mt: 3, bgcolor: 'primary.lighter' }}>
        <Typography variant="body2">
          <strong>Can't find what you're looking for?</strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Contact our support team for personalized assistance with your specific question.
        </Typography>
      </Paper>
    </Box>
  )
}

export default FAQ